import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { BUSINESS_NAME_LEGAL as BUSINESS_NAME, PHONE, EMAIL, ORG_SCHEMA } from "@/lib/constants";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://truamericanplumbing.com/" },
    { "@type": "ListItem", position: 2, name: "Privacy Policy", item: "https://truamericanplumbing.com/privacy-policy" },
  ],
};

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description={`Privacy Policy for ${BUSINESS_NAME}. Learn how we collect, use, and protect your personal information including SMS messaging data.`}
        canonicalUrl="/privacy-policy/"
        schema={[ORG_SCHEMA, breadcrumbSchema]}
      />

      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-10">
            Last Updated: February 24, 2026
          </p>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">1. Introduction</h2>
              <p>
                {BUSINESS_NAME} ("we," "us," or "our") respects your privacy and is committed
                to protecting your personal information. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you visit our
                website or use our services, including our SMS messaging program.
              </p>
              <p className="mt-3">
                By using our website or services, you agree to the terms of this Privacy Policy.
                If you do not agree, please do not use our website or services.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Personal Information:</strong> Name, phone number, email address, and mailing address when you submit a service request or contact us.</li>
                <li><strong>Service Information:</strong> Details about the plumbing or water purification services you request.</li>
                <li><strong>SMS Consent Data:</strong> Your opt-in consent to receive SMS messages from us.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our website, including IP address, browser type, pages visited, and time spent on pages.</li>
                <li><strong>Cookies:</strong> Small data files stored on your device to improve your browsing experience.</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Provide, maintain, and improve our plumbing and water purification services</li>
                <li>Schedule appointments and send service confirmations</li>
                <li>Send SMS notifications including appointment reminders, service updates, and scheduling confirmations</li>
                <li>Respond to your inquiries and customer service requests</li>
                <li>Send emergency service alerts when applicable</li>
                <li>Improve our website and customer experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            {/* SMS/Text Messaging */}
            <div className="bg-muted border border-border rounded-xl p-6">
              <h2 className="text-2xl font-bold text-foreground mb-3">4. SMS/Text Messaging Privacy</h2>
              <p className="font-semibold text-foreground">
                No mobile information will be shared with third parties/affiliates for
                marketing/promotional purposes. Information sharing to subcontractors in
                support services, such as customer service, is permitted. All other use case
                categories exclude text messaging originator opt-in data and consent; this
                information will not be shared with any third parties, excluding aggregators
                and providers of the Text Message services.
              </p>
              <p className="mt-4">
                When you opt in to our SMS program, we collect your mobile phone number and
                consent status. This data is used solely for the purpose of sending you
                service-related text messages as described in our{" "}
                <Link to="/terms-of-service/" className="text-primary hover:text-accent underline">
                  Terms of Service
                </Link>.
              </p>
              <p className="mt-3">
                You may opt out of SMS messages at any time by replying <strong>STOP</strong> to
                any message. For help, text <strong>HELP</strong> or contact us at{" "}
                <a href={`tel:${PHONE.replace(/-/g, "")}`} className="text-primary hover:text-accent underline">{PHONE}</a>.
              </p>
            </div>

            {/* Information Sharing */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">5. Information Sharing and Disclosure</h2>
              <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Service Providers:</strong> With trusted subcontractors and service providers who assist us in delivering our services, subject to confidentiality agreements.</li>
                <li><strong>SMS Service Providers:</strong> With aggregators and providers of text message services solely for the purpose of delivering SMS messages you have opted in to receive.</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                <li><strong>Business Protection:</strong> To protect the rights, property, or safety of {BUSINESS_NAME}, our customers, or others.</li>
              </ul>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">6. Cookies and Tracking Technologies</h2>
              <p>
                Our website may use cookies and similar tracking technologies to enhance your
                experience. You can control cookies through your browser settings. Disabling
                cookies may affect certain features of our website.
              </p>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">7. Data Security</h2>
              <p>
                We implement reasonable administrative, technical, and physical security
                measures to protect your personal information from unauthorized access,
                alteration, disclosure, or destruction. However, no method of transmission
                over the Internet or electronic storage is 100% secure.
              </p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">8. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill
                the purposes outlined in this Privacy Policy, unless a longer retention period
                is required or permitted by law. SMS opt-in consent records are retained for
                the duration of your participation in our messaging program and for a
                reasonable period thereafter.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">9. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal obligations.</li>
                <li><strong>Opt-Out:</strong> Opt out of SMS messages at any time by texting STOP.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us using the information below.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">10. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 18. We do not
                knowingly collect personal information from children.
              </p>
            </div>

            {/* Changes */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted
                on this page with an updated "Last Updated" date. Your continued use of our
                website or services after changes constitutes your acceptance of the updated
                Privacy Policy.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">12. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <ul className="list-none mt-3 space-y-1">
                <li><strong>{BUSINESS_NAME}</strong></li>
                <li>Tolleson, AZ — Serving the West Valley</li>
                <li>Phone: <a href={`tel:${PHONE.replace(/-/g, "")}`} className="text-primary hover:text-accent underline">{PHONE}</a></li>
                <li>Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:text-accent underline">{EMAIL}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
